// Per-kind merged BufferGeometry cache for batched voxel rendering (P0.1-full).
//
// Each prefab's voxels are baked into a single BufferGeometry with per-vertex
// color + roughness + metalness + emissive (P3.3). The batched renderer then
// draws every entity sharing that prefab with one InstancedMesh, where each
// instance is the whole prefab (vs the old per-entity InstancedMesh where
// each instance was a single voxel).
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.
import type { BlockVoxel } from '@aod/ui/prefabs';
import { VOXEL_SCALE, getBuildingPrefab, getCreaturePrefab } from '@aod/ui/prefabs';
import type * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { smoothPartGeometry } from './smoothSurface.js';
import {
  DEFAULT_METALNESS,
  DEFAULT_ROUGHNESS,
  boxForVoxel,
  partCapsule,
  partSphere,
} from './voxelGeometryParts.js';
import {
  BODY_PART_RE,
  applyProportions,
  applyVerticalShade,
  applyWear,
  seedOf,
  teamTrim,
} from './voxelGeometryShaping.js';

// The attribute baking and the hi-fi shaping live in sibling modules; the
// shaping helpers are re-exported so existing imports keep working.
export { applyProportions, applyWear, teamTrim } from './voxelGeometryShaping.js';

const creatureGeoCache = new Map<string, THREE.BufferGeometry>();
const buildingGeoCache = new Map<string, THREE.BufferGeometry>();

/** Primitive-substitution rules for the hi-fi path (part name → shape). */
function hiFiPartGeometry(
  name: string,
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  // Parts carrying per-voxel semantics (team tabards, glowing runes) keep
  // the per-voxel rounded boxes — primitives can't express them.
  const hasSpecial = voxels.some((v) => v.team || v.emissive);
  // Every part becomes one smooth surface (heads and limbs too: a face, a boot or a claw is
  // colour on the surface, and a single-tint sphere or capsule would erase it); per-voxel
  // team/glow flags ride on the vertices. Parts too small to smooth fall through below.
  const smooth = smoothPartGeometry(voxels, legacyTint, {
    roughness: DEFAULT_ROUGHNESS,
    metalness: DEFAULT_METALNESS,
  });
  if (smooth) {
    const trim = hasSpecial && !legacyTint ? teamTrim(voxels) : null;
    return trim ? mergeGeometries([smooth, trim], false) : smooth;
  }
  if (hasSpecial) {
    const parts = voxels.map((v) => boxForVoxel(v, legacyTint, true, true));
    const trim = legacyTint ? null : teamTrim(voxels);
    if (trim) parts.push(trim);
    return parts.length > 0 ? mergeGeometries(parts, false) : null;
  }
  if (/head/i.test(name)) return partSphere(voxels, legacyTint);
  if (/(arm|leg|wing)/i.test(name)) return partCapsule(voxels, legacyTint);
  return null; // torso/detail: fall through to per-voxel rounded boxes
}

function buildCreatureGeometry(prefabId: string, hiFi = false): THREE.BufferGeometry | null {
  const prefab = getCreaturePrefab(prefabId);
  if (!prefab) return null;

  const legacyTint = !prefab.parts.some((part) => part.voxels.some((v) => v.team));

  if (hiFi) {
    // Primitive-substitution path: heads → spheres, limbs → capsules,
    // everything else (torso/detail/per-voxel-semantics) → rounded boxes.
    // Detail-overdraw (the later-wins dedupe below) doesn't apply — each
    // part renders once, whole.
    // The unit's vertical axis (for the species build): the centre of every body voxel.
    let sumX = 0;
    let sumZ = 0;
    let n = 0;
    for (const part of prefab.parts) {
      if (!BODY_PART_RE.test(part.name)) continue;
      for (const v of part.voxels) {
        sumX += (v.x + 0.5) * VOXEL_SCALE;
        sumZ += (v.z + 0.5) * VOXEL_SCALE;
        n++;
      }
    }
    const cx = n > 0 ? sumX / n : 0;
    const cz = n > 0 ? sumZ / n : 0;
    const parts: THREE.BufferGeometry[] = [];
    for (const part of prefab.parts) {
      if (part.voxels.length === 0) continue;
      const prim = hiFiPartGeometry(part.name, part.voxels, legacyTint);
      let geo: THREE.BufferGeometry;
      if (prim) {
        geo = prim;
      } else {
        const boxes = part.voxels.map((v) => boxForVoxel(v, legacyTint, true, true));
        geo = boxes.length === 1 ? boxes[0]! : mergeGeometries(boxes, false);
      }
      applyProportions(geo, part.name, prefab.hifi, cx, cz);
      parts.push(geo);
    }
    if (parts.length === 0) return null;
    const merged = mergeGeometries(parts, false);
    applyWear(merged, seedOf(prefabId));
    applyVerticalShade(merged);
    return merged;
  }

  // Later voxels win at a shared position: prefabs draw detail (trim,
  // helmets over heads, sails over masts, aprons over torsos) after the base
  // shape. First-wins silently dropped that detail on 31 of 44 prefabs.
  const byPosition = new Map<string, BlockVoxel>();
  for (const part of prefab.parts) {
    for (const v of part.voxels) byPosition.set(`${v.x},${v.y},${v.z}`, v);
  }
  const parts = [...byPosition.values()].map((v) => boxForVoxel(v, legacyTint, false, true));
  if (parts.length === 0) return null;
  return mergeGeometries(parts, false);
}

function buildBuildingGeometry(prefabId: string): THREE.BufferGeometry | null {
  const prefab = getBuildingPrefab(prefabId);
  if (!prefab) return null;

  const legacyTint = !prefab.blocks.some((b) => b.team);
  // Later blocks win at a shared position (same rule as creatures).
  const byPosition = new Map<string, BlockVoxel>();
  for (const b of prefab.blocks) byPosition.set(`${b.x},${b.y},${b.z}`, b);
  const parts = [...byPosition.values()].map((b) => boxForVoxel(b, legacyTint));
  if (parts.length === 0) return null;
  return mergeGeometries(parts, false);
}

/**
 * Cached creature (unit) geometry lookup. Returns null for unknown/empty
 * prefabs. `hiFi` selects the rounded, smooth-normaled variant (separate
 * cache slot — the toggle swaps prefabs without rebuilding the lo-fi set).
 */
export function getCreatureGeometry(prefabId: string, hiFi = false): THREE.BufferGeometry | null {
  const key = `${hiFi ? 'hi' : 'lo'}:${prefabId}`;
  const cached = creatureGeoCache.get(key);
  if (cached) return cached;
  const built = buildCreatureGeometry(prefabId, hiFi);
  if (built) creatureGeoCache.set(key, built);
  return built;
}

/** Cached structure (building) geometry lookup. Returns null for unknown/empty prefabs. */
export function getBuildingGeometry(prefabId: string): THREE.BufferGeometry | null {
  const cached = buildingGeoCache.get(prefabId);
  if (cached) return cached;
  const built = buildBuildingGeometry(prefabId);
  if (built) buildingGeoCache.set(prefabId, built);
  return built;
}
